import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Col, Container, FormGroup, FormLabel, Row, Spinner } from 'react-bootstrap';
import { ErrorMessage, Field, FieldProps, Form as FormikForm, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { ChildTransitionProjectPatch, ChildTransitionProjectPost } from '../../typings/types-to-send';
import { Mode, parseMode } from '../../shared/helpers/modeHelper';
import { useTranslation } from 'react-i18next';
import FormikSelect, {
  createDoesOptionMatchFieldFn,
  GenericOption,
} from '../../shared/formik/FormikSelect/FormikSelect';
import { createChildOptions, useGetChildren } from '../../api/child_api';
import { useGetChildIdFromSearchParams } from '../../shared/helpers/formHelper';
import EditToggle from '../../shared/components/EditToggle/EditToggle';
import DefaultPageActionButtons, {
  useNavigateAfterCancel,
  useNavigateToSuccessPage,
  useOnPageBack,
} from '../../shared/components/DefaultPageActionButtons/DefaultPageActionButtons';
import { Domain, getDomainPermission, hasPagePermission, usePermissions } from '../../api/permissions_api';
import NoPermissionPage from '../../shared/pages/NoPermissionPage/NoPermissionPage';
import ErrorPage from '../../shared/pages/ErrorPage/ErrorPage';
import CustomErrorMessage from '../../shared/formik/CustomErrorMessage/CustomErrorMessage';
import CreatableSelect from 'react-select/creatable';
import FormikTextAreaInput from '../../shared/formik/FormikTextAreaInput/FormikTextAreaInput';
import {
  createTransitionProjectNameOptions,
  deleteTransitionProject,
  patchTransitionProject,
  postTransitionProject,
  useGetExistingTransitionProjectNames,
  useGetTransitionProjectDetail,
} from '../../api/transition_project_api';

type TransitionProjectPageProps = {};

const DOMAIN_URL = 'child-transition-project';
const DOMAIN = Domain.CHILD_TRANSITION_PROJECT;
const CHILD_TAB = 'transition';

const TRANSITION_PROJECT_FORM_SCHEMA = Yup.object().shape({
  child: Yup.number().required('Required'),
  project_name: Yup.string().required('Required'),
  status_text: Yup.string().required('Required'),
});

function getBlankTransitionProject(): ChildTransitionProjectPost {
  return {
    child: undefined,
    project_name: '',
    status_text: '',
  };
}

const TransitionProjectPage = (props: TransitionProjectPageProps) => {
  let { id: idString, mode: modeString } = useParams();
  const childId = useGetChildIdFromSearchParams();
  const { t } = useTranslation();
  const { data: childData, isLoading: isChildrenLoading } = useGetChildren();
  const { data: projectNameData } = useGetExistingTransitionProjectNames();
  const mode = parseMode(modeString);
  const [serverError, setServerError] = useState(false);
  const { isLoadingPermissions, permissionMap } = usePermissions();
  const onBack = useOnPageBack(DOMAIN_URL, CHILD_TAB);
  const navigateToSuccessPage = useNavigateToSuccessPage(DOMAIN_URL, CHILD_TAB);
  const navigateAfterCancel = useNavigateAfterCancel(DOMAIN_URL, CHILD_TAB);

  const hasNumberId = !!idString && !Number.isNaN(parseInt(idString));

  let id: number | undefined = undefined;
  if (idString) {
    id = parseInt(idString);
  }
  const { data, isLoading, error, refetch } = useGetTransitionProjectDetail(id, mode !== Mode.CREATE && hasNumberId);

  if (isLoadingPermissions) {
    return <Spinner animation="border" role="status" />;
  } else if (!permissionMap) {
    return <ErrorPage />;
  } else if (!hasPagePermission(mode, getDomainPermission(permissionMap, DOMAIN))) {
    return <NoPermissionPage />;
  }
  const hasChangePermission = getDomainPermission(permissionMap, DOMAIN).canChange;

  let transitionProject: ChildTransitionProjectPost | ChildTransitionProjectPatch;
  let onSubmit;
  if (mode === Mode.CREATE) {
    transitionProject = getBlankTransitionProject();
    if (isChildrenLoading) {
      return <Spinner animation="border" role="status" />;
    } else if (!childData) {
      return <div>Could not load children</div>;
    }
    if (childId) {
      transitionProject.child = childId;
    }
    onSubmit = async (dataToSubmit: ChildTransitionProjectPost, { setSubmitting }: any) => {
      try {
        await postTransitionProject(dataToSubmit);
        // Let the refresh happen when the new page loads (hopefully!)
        setSubmitting(false);
        navigateToSuccessPage();
      } catch (error) {
        setServerError(true);
      }
    };
  } else if (hasNumberId) {
    if (isLoading) {
      return <Spinner animation="border" role="status" />;
    } else if (!data) {
      return <div>Could not find data for {id}</div>;
    } else if (error) {
      return <div>Error loading {id}</div>;
    } else if (serverError) {
      return <div> Something went wrong, contact LJI IT </div>;
    }

    transitionProject = data as ChildTransitionProjectPatch;

    onSubmit = async (dataToSubmit: ChildTransitionProjectPatch, { setSubmitting }: any) => {
      try {
        await patchTransitionProject(id, dataToSubmit);
        await refetch();
        setSubmitting(false);
        navigateToSuccessPage();
      } catch (error) {
        setServerError(true);
      }
    };
  } else {
    throw Error('Bad id: ' + idString);
  }

  function handleOnCancel(resetForm: (dataToResetTo: any) => void) {
    resetForm(transitionProject);
    navigateAfterCancel();
  }

  const childOptions = createChildOptions(childData);
  const transitionProjectNameOptions = createTransitionProjectNameOptions(projectNameData);

  const onDelete = () => {
    if (id) {
      return deleteTransitionProject(id);
    } else {
      return Promise.resolve();
    }
  };

  const afterDelete = () => {
    if (id) {
      navigateAfterCancel();
    }
  };

  const transitionProjectParamName = 'project_name';
  const transitionProjectParamLabel = t('transitionProject.NAME');
  const transitionProjectParamIsReadOnly = mode === Mode.VIEW;
  const transitionProjectParamLabelWidth = 4;

  return (
    <Formik initialValues={transitionProject} validationSchema={TRANSITION_PROJECT_FORM_SCHEMA} onSubmit={onSubmit}>
      {(formikProps: FormikProps<ChildTransitionProjectPost | ChildTransitionProjectPatch>) => {
        // TODO this should really be an enhancement to FormikSelect
        //  but it is a little unwieldy to add it because FormikSelect supports isMulti
        let creatableSelect = (
          <div>
            {/* This is a formik component so formik does validation and manages state */}
            <Field name={transitionProjectParamName}>
              {(fieldProps: FieldProps) => {
                const { field, form } = fieldProps;
                const handleChange = (option: any) => {
                  let value;
                  if (option === undefined || option === null) {
                    // Purposely send null so the backend clears the value (django PATCH ignores undefineds by default)
                    value = null;
                  } else {
                    value = (option as GenericOption).value;
                  }
                  form.setFieldValue(field.name, value);
                };
                const doesOptionMatchField = createDoesOptionMatchFieldFn(field);
                const getValue = () => {
                  return transitionProjectNameOptions.find(doesOptionMatchField);
                };

                let reactSelectComponent;
                if (transitionProjectParamIsReadOnly) {
                  if (!getValue()) {
                    reactSelectComponent = <div />;
                  } else {
                    reactSelectComponent = <Col className="d-flex align-items-center">{getValue()?.label}</Col>;
                  }
                } else {
                  reactSelectComponent = (
                    <Col>
                      <CreatableSelect
                        placeholder={'Select or start typing to create'}
                        name={field.name}
                        value={getValue()}
                        onChange={handleChange}
                        options={transitionProjectNameOptions}
                        isClearable={true}
                      />
                      {/* Not getting errors messages?
                        Try adding 'fieldName: undefined,' to initialStateOfEditableScheduleFields

                        Still not working and in AG grid? Try setting row height to 50
                      */}
                      <ErrorMessage name={transitionProjectParamName}>
                        {(errorMessage) => {
                          return <CustomErrorMessage errorMessage={errorMessage} />;
                        }}
                      </ErrorMessage>
                    </Col>
                  );
                }
                return (
                  <FormGroup as={Row}>
                    {transitionProjectParamLabel && (
                      // This is a react-bootstrap component match label look-and-feel
                      <FormLabel column={true} sm={transitionProjectParamLabelWidth}>
                        {transitionProjectParamLabel}
                      </FormLabel>
                    )}
                    {reactSelectComponent}
                  </FormGroup>
                );
              }}
            </Field>
          </div>
        );

        return (
          <FormikForm>
            <Container>
              <Row>
                <Col>
                  <h1 className="text-center font-weight-bold">{t('transitionProject.DETAIL_TITLE')}</h1>
                </Col>
              </Row>
              <Row className={'shadow'}>
                <Col sm={10}>
                  <FormikSelect
                    name={'child'}
                    label={t('common.CHILD')}
                    options={childOptions}
                    isReadOnly={mode === Mode.VIEW}
                    labelWidth={4}
                    showSideBySide={true}
                  />
                  {creatableSelect}
                  <FormikTextAreaInput
                    name={'status_text'}
                    label={t('transitionProject.STATUS_TEXT')}
                    formikProps={formikProps}
                    isReadOnly={mode === Mode.VIEW}
                    labelWidth={4}
                    showSideBySide={true}
                  />
                </Col>
                <Col sm={2}>
                  <EditToggle
                    show={hasChangePermission}
                    mode={mode}
                    formikProps={formikProps}
                    toEditLink={{
                      pathname: '../transition-project/edit/' + idString,
                      search: childId ? '?childId=' + childId : undefined,
                    }}
                    onCancel={handleOnCancel.bind(null, formikProps.resetForm)}
                  />
                </Col>
                <DefaultPageActionButtons
                  mode={mode}
                  formikProps={formikProps}
                  onCancelEdit={handleOnCancel.bind(null, formikProps.resetForm)}
                  onDelete={onDelete}
                  afterDelete={afterDelete}
                  onBack={onBack}
                />
              </Row>
            </Container>
          </FormikForm>
        );
      }}
    </Formik>
  );
};

export default TransitionProjectPage;
